/**
 * Color Map for AI Presentation Layer Editing
 *
 * Maps natural language color names to RGBA values.
 * Used by AI to interpret color-related commands.
 */

import { CommandColor } from './CommandSchema';

/**
 * Named color mapping for natural language color references
 */
export const COLOR_MAP: Record<string, CommandColor> = {
  // Basic colors
  black: { r: 0, g: 0, b: 0, a: 1 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  red: { r: 239, g: 68, b: 68, a: 1 },
  green: { r: 34, g: 197, b: 94, a: 1 },
  blue: { r: 59, g: 130, b: 246, a: 1 },
  yellow: { r: 250, g: 204, b: 21, a: 1 },
  orange: { r: 249, g: 115, b: 22, a: 1 },
  purple: { r: 168, g: 85, b: 247, a: 1 },
  pink: { r: 236, g: 72, b: 153, a: 1 },
  gray: { r: 107, g: 114, b: 128, a: 1 },
  grey: { r: 107, g: 114, b: 128, a: 1 }, // British spelling alias

  // Shades
  'dark blue': { r: 30, g: 58, b: 138, a: 1 },
  'light blue': { r: 147, g: 197, b: 253, a: 1 },
  'dark green': { r: 21, g: 128, b: 61, a: 1 },
  'light green': { r: 134, g: 239, b: 172, a: 1 },
  'dark red': { r: 153, g: 27, b: 27, a: 1 },
  'light red': { r: 254, g: 202, b: 202, a: 1 },
  'dark gray': { r: 55, g: 65, b: 81, a: 1 },
  'dark grey': { r: 55, g: 65, b: 81, a: 1 },
  'light gray': { r: 209, g: 213, b: 219, a: 1 },
  'light grey': { r: 209, g: 213, b: 219, a: 1 },

  // Named shades
  navy: { r: 30, g: 41, b: 59, a: 1 },
  teal: { r: 20, g: 184, b: 166, a: 1 },
  cyan: { r: 6, g: 182, b: 212, a: 1 },
  indigo: { r: 99, g: 102, b: 241, a: 1 },
  violet: { r: 139, g: 92, b: 246, a: 1 },
  magenta: { r: 217, g: 70, b: 239, a: 1 },
  rose: { r: 244, g: 63, b: 94, a: 1 },
  amber: { r: 245, g: 158, b: 11, a: 1 },
  lime: { r: 132, g: 204, b: 22, a: 1 },
  emerald: { r: 16, g: 185, b: 129, a: 1 },
  sky: { r: 14, g: 165, b: 233, a: 1 },
  slate: { r: 100, g: 116, b: 139, a: 1 },
  zinc: { r: 113, g: 113, b: 122, a: 1 },
  stone: { r: 120, g: 113, b: 108, a: 1 },
  neutral: { r: 115, g: 115, b: 115, a: 1 },

  // Semantic colors
  primary: { r: 99, g: 102, b: 241, a: 1 },    // Indigo-like
  secondary: { r: 107, g: 114, b: 128, a: 1 },  // Gray
  success: { r: 34, g: 197, b: 94, a: 1 },      // Green
  warning: { r: 245, g: 158, b: 11, a: 1 },     // Amber
  danger: { r: 239, g: 68, b: 68, a: 1 },       // Red
  error: { r: 239, g: 68, b: 68, a: 1 },        // Red alias
  info: { r: 59, g: 130, b: 246, a: 1 },        // Blue

  // Transparent
  transparent: { r: 0, g: 0, b: 0, a: 0 },
  clear: { r: 0, g: 0, b: 0, a: 0 },

  // Brand/special colors
  gold: { r: 212, g: 175, b: 55, a: 1 },
  silver: { r: 192, g: 192, b: 192, a: 1 },
  bronze: { r: 205, g: 127, b: 50, a: 1 },
  coral: { r: 255, g: 127, b: 80, a: 1 },
  salmon: { r: 250, g: 128, b: 114, a: 1 },
  turquoise: { r: 64, g: 224, b: 208, a: 1 },
  aqua: { r: 0, g: 255, b: 255, a: 1 },
  maroon: { r: 128, g: 0, b: 0, a: 1 },
  olive: { r: 128, g: 128, b: 0, a: 1 },
  tan: { r: 210, g: 180, b: 140, a: 1 },
  beige: { r: 245, g: 245, b: 220, a: 1 },
  ivory: { r: 255, g: 255, b: 240, a: 1 },
  cream: { r: 255, g: 253, b: 208, a: 1 },
  charcoal: { r: 54, g: 69, b: 79, a: 1 },
  midnight: { r: 25, g: 25, b: 112, a: 1 },
};

/**
 * Parse a color from a string (name or hex)
 * Returns null if color cannot be parsed
 */
export function parseColor(colorString: string): CommandColor | null {
  if (!colorString) return null;

  const normalized = colorString.toLowerCase().trim();

  // Check named colors first
  if (COLOR_MAP[normalized]) {
    return { ...COLOR_MAP[normalized] };
  }

  // Try to parse as hex color
  const hexMatch = normalized.match(/^#?([a-f0-9]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: 1,
    };
  }

  // Try to parse as hex with alpha (8 chars)
  const hexAlphaMatch = normalized.match(/^#?([a-f0-9]{8})$/i);
  if (hexAlphaMatch) {
    const hex = hexAlphaMatch[1];
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
      a: parseInt(hex.slice(6, 8), 16) / 255,
    };
  }

  // Try to parse as rgb(r, g, b) or rgba(r, g, b, a)
  const rgbMatch = normalized.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
    };
  }

  return null;
}

/**
 * Get closest named color for a given color
 * Useful for generating descriptions
 */
export function getClosestColorName(color: CommandColor): string {
  let closestName = 'unknown';
  let closestDistance = Infinity;

  for (const [name, namedColor] of Object.entries(COLOR_MAP)) {
    // Skip transparent for distance calculation unless the color is actually transparent
    if (name === 'transparent' || name === 'clear') {
      if (color.a === 0) return 'transparent';
      continue;
    }

    const distance = Math.sqrt(
      Math.pow(color.r - namedColor.r, 2) +
      Math.pow(color.g - namedColor.g, 2) +
      Math.pow(color.b - namedColor.b, 2)
    );

    if (distance < closestDistance) {
      closestDistance = distance;
      closestName = name;
    }
  }

  return closestName;
}

/**
 * Check if a color is "dark" (for contrast decisions)
 */
export function isDarkColor(color: CommandColor): boolean {
  // Calculate luminance using standard formula
  const luminance = (0.299 * color.r + 0.587 * color.g + 0.114 * color.b) / 255;
  return luminance < 0.5;
}

/**
 * Get a contrasting color (black or white) for text on a given background
 */
export function getContrastingColor(backgroundColor: CommandColor): CommandColor {
  return isDarkColor(backgroundColor)
    ? { r: 255, g: 255, b: 255, a: 1 }
    : { r: 0, g: 0, b: 0, a: 1 };
}

/**
 * Lighten a color by a percentage
 */
export function lightenColor(color: CommandColor, percent: number): CommandColor {
  const factor = percent / 100;
  return {
    r: Math.min(255, Math.round(color.r + (255 - color.r) * factor)),
    g: Math.min(255, Math.round(color.g + (255 - color.g) * factor)),
    b: Math.min(255, Math.round(color.b + (255 - color.b) * factor)),
    a: color.a,
  };
}

/**
 * Darken a color by a percentage
 */
export function darkenColor(color: CommandColor, percent: number): CommandColor {
  const factor = 1 - percent / 100;
  return {
    r: Math.round(color.r * factor),
    g: Math.round(color.g * factor),
    b: Math.round(color.b * factor),
    a: color.a,
  };
}

/**
 * Convert CommandColor to CSS rgba string
 */
export function colorToCss(color: CommandColor): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
}

/**
 * Convert CommandColor to iOS hex string (RRGGBBAA)
 */
export function colorToIosHex(color: CommandColor): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}${toHex(color.a * 255)}`.toUpperCase();
}
